/**
 * Typed client for /pipeline/* — backs the Onboarding Pipeline screen.
 *
 * This file covers the daily-use paths (overview, funnel, prospects
 * list + detail, manual add, pause/resume sequence). The agentic
 * controls (run-now, agentic config, outreach stats) live on the
 * still-legacy /outreach sub-tab and will be ported in a follow-up
 * session — leaving them out keeps this port shippable in one go.
 */
import { client } from './client';

export type OnboardingStateName =
  | 'interested'
  | 'email_sent'
  | 'link_clicked'
  | 'form_started'
  | 'form_complete'
  | 'account_created'
  | 'keys_issued'
  | 'active';

export type ProspectStatus =
  | 'new'
  | 'contacted'
  | 'interested'
  | 'registered'
  | 'active'
  | 'dormant'
  | 'churned';

export type TrafficTier = 'small' | 'medium' | 'large' | 'enterprise';
export type ProspectSource = 'apollo' | 'manual' | 'referral';

export const ONBOARDING_STATES: OnboardingStateName[] = [
  'interested',
  'email_sent',
  'link_clicked',
  'form_started',
  'form_complete',
  'account_created',
  'keys_issued',
  'active'
];

export const PROSPECT_STATUSES: ProspectStatus[] = [
  'new',
  'contacted',
  'interested',
  'registered',
  'active',
  'dormant',
  'churned'
];

export interface OnboardingStateRecord {
  _id: string;
  prospectId: string;
  state: OnboardingStateName;
  stateEnteredAt?: string;
  nudgeCount?: number;
  lastNudgeAt?: string | null;
  notes?: string;
}

export interface Prospect {
  _id: string;
  domain: string;
  contactEmail?: string;
  contactName?: string;
  companyName?: string;
  monthlyTrafficTier?: TrafficTier;
  contentCategory?: string;
  score?: number;
  status: ProspectStatus;
  source?: ProspectSource;
  notes?: string;
  sequencePaused?: boolean;
  sequencePausedAt?: string | null;
  sequencePausedReason?: string | null;
  createdAt: string;
  updatedAt: string;
  // Attached by the list endpoint
  onboardingState?: OnboardingStateRecord | null;
}

export interface OutreachStep {
  _id: string;
  prospectId: string;
  sequenceStep: number;
  emailSentAt?: string;
  emailSubject?: string;
  emailBody?: string;
  replyReceived?: boolean;
  replyIntent?: 'interested' | 'not_interested' | 'question' | 'other';
  replyText?: string;
}

export interface ProspectEscalation {
  _id: string;
  status: 'open' | 'assigned' | 'resolved' | 'dismissed';
  reason: string;
  createdAt: string;
}

export interface PipelineOverviewResponse {
  onboardingStates: Partial<Record<OnboardingStateName, number>>;
  prospectStatuses: Partial<Record<ProspectStatus, number>>;
  openEscalations: number;
  transitionsLast24h: number;
  totalProspects: number;
}

export interface PipelineFunnelResponse {
  funnel: Partial<Record<OnboardingStateName, number>>;
  current: Partial<Record<OnboardingStateName, number>>;
}

export interface ProspectListResponse {
  docs: Prospect[];
  totalDocs: number;
  limit: number;
  page: number;
  totalPages: number;
}

export interface ProspectListParams {
  page?: number;
  limit?: number;
  status?: ProspectStatus | '';
  state?: OnboardingStateName | '';
  sort?: string;
  q?: string;
}

export interface ProspectDetailResponse {
  prospect: Prospect;
  onboarding: OnboardingStateRecord | null;
  outreach: OutreachStep[];
  escalations: ProspectEscalation[];
}

export interface CreateProspectInput {
  domain: string;
  contactEmail?: string;
  contactName?: string;
  companyName?: string;
  monthlyTrafficTier?: TrafficTier;
  source?: ProspectSource;
}

// ─── Fetchers ─────────────────────────────────────────────────────────────

export async function fetchPipelineOverview(): Promise<PipelineOverviewResponse> {
  const r = await client.get<PipelineOverviewResponse>('/pipeline/overview');
  return r.data;
}

export async function fetchPipelineFunnel(): Promise<PipelineFunnelResponse> {
  const r = await client.get<PipelineFunnelResponse>('/pipeline/funnel');
  return r.data;
}

export async function fetchProspects(
  params: ProspectListParams
): Promise<ProspectListResponse> {
  const cleanParams: Record<string, unknown> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== '' && v !== undefined && v !== null) {
      cleanParams[k] = v;
    }
  });
  const r = await client.get<ProspectListResponse>('/pipeline/prospects', {
    params: cleanParams
  });
  return r.data;
}

export async function fetchProspectDetail(
  id: string
): Promise<ProspectDetailResponse> {
  const r = await client.get<ProspectDetailResponse>(`/pipeline/prospects/${id}`);
  return r.data;
}

export async function createProspect(input: CreateProspectInput): Promise<Prospect> {
  const r = await client.post<Prospect>('/pipeline/prospects', input);
  return r.data;
}

export async function pauseProspect(
  id: string,
  reason?: string
): Promise<Prospect> {
  const r = await client.post<Prospect>(`/pipeline/prospects/${id}/pause`, {
    reason
  });
  return r.data;
}

export async function resumeProspect(id: string): Promise<Prospect> {
  const r = await client.post<Prospect>(`/pipeline/prospects/${id}/resume`);
  return r.data;
}

// ─── Agentic controls: config (kill switch + ICP) + run-now ────────────────

export interface AgenticIcp {
  industries?: string[];
  titles?: string[];
  companySizeMin?: number;
  companySizeMax?: number;
  maxResults?: number;
}

export interface AgenticVendorStatus {
  apollo: { mode: string };
  email: { provider: string; mode: string };
  anthropic: { mode: string };
}

export interface AgenticModelStatus {
  active: string; // model id currently in effect
  standard: string; // model used when economy is off
  economy: string; // model used when economy (Haiku) is on
}

export interface AgenticConfig {
  disabled: boolean;
  envDisabled: boolean;
  effectiveDisabled: boolean;
  disabledAt?: string | null;
  disabledReason?: string | null;
  icp: AgenticIcp; // resolved/effective ICP
  icpOverrides: AgenticIcp; // raw stored overrides (what Save writes)
  lastUpdatedAt?: string;
  economyMode: boolean; // the "Haiku switch" (db flag)
  envEconomy: boolean; // AGENTIC_ECONOMY_MODE=true forces it on
  model: AgenticModelStatus;
  vendors: AgenticVendorStatus;
}

export interface UpdateAgenticConfigInput {
  disabled?: boolean;
  disabledReason?: string;
  economyMode?: boolean;
  icp?: AgenticIcp;
}

export interface RunResult {
  ok: boolean;
  [k: string]: unknown;
}

export async function fetchAgenticConfig(): Promise<AgenticConfig> {
  const r = await client.get<AgenticConfig>('/pipeline/agentic-config');
  return r.data;
}

export async function updateAgenticConfig(
  input: UpdateAgenticConfigInput
): Promise<void> {
  await client.patch('/pipeline/agentic-config', input);
}

export async function runProspecting(dryRun = false): Promise<RunResult> {
  const r = await client.post<RunResult>('/pipeline/agentic/run-prospecting', {
    dryRun
  });
  return r.data;
}

export async function runSend(): Promise<RunResult> {
  const r = await client.post<RunResult>('/pipeline/agentic/run-send');
  return r.data;
}
